import React, { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { compressImage } from '../../lib/imageCompression';
import { Button } from './button';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Componente reutilizable para subir imágenes desde el dispositivo.
 * Props:
 * - value: file_id actual (string) o lista de file_ids (array)
 * - onChange: (fileIds) => void
 * - multiple: boolean
 * - purpose: 'product' | 'company_logo' | 'general'
 * - label: texto del label
 */
export default function ImageUploader({ value, onChange, multiple = false, purpose = 'general', label = 'Imagen' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const values = Array.isArray(value) ? value : (value ? [value] : []);

  const upload = async (file) => {
    const compressed = await compressImage(file, { maxDimension: 1600, quality: 0.82 });
    const fd = new FormData();
    fd.append('file', compressed);
    const res = await api.uploadFile(fd, purpose);
    return res.data.file_id;
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const id = await upload(f);
        ids.push(id);
      }
      const next = multiple ? [...values, ...ids] : ids[ids.length - 1];
      onChange(next);
      toast.success(`SalesPRO: ${ids.length} imagen(es) subida(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SalesPRO: Error al subir imagen');
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const removeOne = (id) => {
    const next = values.filter(v => v !== id);
    onChange(multiple ? next : (next[0] || ''));
  };

  return (
    <div>
      <label className="text-sm font-medium text-[#022C22]">{label}</label>
      <div className="mt-2 space-y-2">
        {values.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {values.map(id => (
              <div key={id} className="relative w-20 h-20 bg-[#F1F5F9] rounded border border-[#E2E8F0] overflow-hidden">
                <img src={api.fileUrl(id)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeOne(id)}
                  className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5 shadow"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFiles}
          className="hidden"
          data-testid="image-upload-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full sm:w-auto"
          data-testid="image-upload-button"
        >
          {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
          <span className="notranslate">{uploading ? 'Subiendo...' : (multiple ? 'Subir imágenes' : (values.length > 0 ? 'Cambiar imagen' : 'Subir imagen'))}</span>
        </Button>
        <p className="text-xs text-[#94A3B8]">JPG, PNG, WEBP, GIF. Se optimizan automáticamente para cargar más rápido.</p>
      </div>
    </div>
  );
}
